/**
 * Product management page with SPU/SKU listing.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DataTable, Column } from '@/components/DataTable'
import { formatCurrency, formatDate, getStatusConfig } from '@/lib/formatters'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useProducts, Product, SKU } from '@/hooks/useProducts'

interface ProductFormData {
  name: string
  description: string
  type: 'one_time' | 'subscription'
}

interface SKUFormData {
  name: string
  price: string
  currency: string
  inventory: string
}

export default function ProductsPage() {
  const { products, loading, error, refresh, createProduct, createSKU, updateSKU, deleteSKU } = useProducts()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isSKUOpen, setIsSKUOpen] = useState(false)
  const [isDeleteSKUOpen, setIsDeleteSKUOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedSKU, setSelectedSKU] = useState<SKU | null>(null)
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set())
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    type: 'one_time',
  })
  const [skuFormData, setSKUFormData] = useState<SKUFormData>({
    name: '',
    price: '',
    currency: 'CNY',
    inventory: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Product name is required', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      await createProduct(formData)
      toast({ title: 'Success', description: 'Product created successfully', variant: 'success' })
      setIsCreateOpen(false)
      setFormData({ name: '', description: '', type: 'one_time' })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create product',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddSKU = (product: Product) => {
    setSelectedProduct(product)
    setSelectedSKU(null)
    setSKUFormData({ name: '', price: '', currency: 'CNY', inventory: '' })
    setIsSKUOpen(true)
  }

  const handleEditSKU = (product: Product, sku: SKU) => {
    setSelectedProduct(product)
    setSelectedSKU(sku)
    setSKUFormData({
      name: sku.name,
      price: (sku.price / 100).toString(),
      currency: sku.currency,
      inventory: sku.inventory?.toString() || '',
    })
    setIsSKUOpen(true)
  }

  const handleSKUSubmit = async () => {
    if (!selectedProduct) return
    if (!skuFormData.name.trim()) {
      toast({ title: 'Error', description: 'SKU name is required', variant: 'destructive' })
      return
    }
    if (!skuFormData.price || isNaN(parseFloat(skuFormData.price))) {
      toast({ title: 'Error', description: 'Valid price is required', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      const data = {
        name: skuFormData.name,
        price: Math.round(parseFloat(skuFormData.price) * 100), // Convert to cents
        currency: skuFormData.currency,
        inventory: skuFormData.inventory ? parseInt(skuFormData.inventory) : null,
      }

      if (selectedSKU) {
        // Show warning if price is changing
        if (data.price !== selectedSKU.price) {
          toast({
            title: 'Note',
            description: 'Price changes will only affect new orders',
            variant: 'default',
          })
        }
        await updateSKU(selectedProduct.id, selectedSKU.id, data)
        toast({ title: 'Success', description: 'SKU updated successfully', variant: 'success' })
      } else {
        await createSKU(selectedProduct.id, data)
        toast({ title: 'Success', description: 'SKU created successfully', variant: 'success' })
      }
      setIsSKUOpen(false)
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save SKU',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSKU = async () => {
    if (!selectedProduct || !selectedSKU) return

    setIsSubmitting(true)
    try {
      await deleteSKU(selectedProduct.id, selectedSKU.id)
      toast({ title: 'Success', description: 'SKU deleted successfully', variant: 'success' })
      setIsDeleteSKUOpen(false)
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to delete SKU',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleExpand = (productId: number) => {
    const newExpanded = new Set(expandedProducts)
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId)
    } else {
      newExpanded.add(productId)
    }
    setExpandedProducts(newExpanded)
  }

  const columns: Column<Product>[] = [
    {
      key: 'expand',
      header: '',
      render: (product) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            toggleExpand(product.id)
          }}
        >
          {expandedProducts.has(product.id) ? '-' : '+'}
        </Button>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
    },
    {
      key: 'type',
      header: 'Type',
      render: (product) => (
        <Badge variant={product.type === 'subscription' ? 'info' : 'secondary'}>
          {product.type === 'subscription' ? 'Subscription' : 'One-time'}
        </Badge>
      ),
    },
    {
      key: 'skus',
      header: 'SKUs',
      render: (product) => product.skus?.length || 0,
    },
    {
      key: 'status',
      header: 'Status',
      render: (product) => {
        const config = getStatusConfig(product.status)
        return <Badge className={config.className}>{config.label}</Badge>
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (product) => formatDate(product.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (product) => (
        <Button variant="outline" size="sm" onClick={() => handleAddSKU(product)}>
          Add SKU
        </Button>
      ),
    },
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground mt-2">
            Manage your products and SKUs
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>Create Product</Button>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={refresh}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Products</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={products}
            columns={columns}
            keyField="id"
            searchPlaceholder="Search products..."
            searchFields={['name', 'description']}
            isLoading={loading}
            emptyMessage="No products found. Create your first product to get started."
          />

          {/* Expanded SKU rows */}
          {products.filter(p => expandedProducts.has(p.id)).map(product => (
            <div key={`skus-${product.id}`} className="mt-4 ml-8 p-4 border rounded-lg bg-muted/30">
              <h4 className="font-medium mb-3">SKUs for {product.name}</h4>
              {product.skus && product.skus.length > 0 ? (
                <div className="space-y-2">
                  {product.skus.map(sku => (
                    <div key={sku.id} className="flex items-center justify-between p-3 bg-background rounded border">
                      <div className="flex-1">
                        <span className="font-medium">{sku.name}</span>
                        <span className="ml-4 text-muted-foreground">
                          {formatCurrency(sku.price, sku.currency)}
                        </span>
                        {sku.inventory !== null && (
                          <span className="ml-4 text-muted-foreground">
                            Stock: {sku.inventory}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSKU(product, sku)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedProduct(product)
                            setSelectedSKU(sku)
                            setIsDeleteSKUOpen(true)
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No SKUs yet. Click "Add SKU" to create one.</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Create Product Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Product</DialogTitle>
            <DialogDescription>
              Add a new product to your catalog. You can add SKUs after creating the product.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Product name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as 'one_time' | 'subscription' })}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SKU Dialog */}
      <Dialog open={isSKUOpen} onOpenChange={setIsSKUOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedSKU ? 'Edit SKU' : 'Add SKU'}</DialogTitle>
            <DialogDescription>
              {selectedSKU
                ? 'Update the SKU details. Price changes will only affect new orders.'
                : `Add a new SKU to "${selectedProduct?.name}".`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sku-name">SKU Name</Label>
              <Input
                id="sku-name"
                value={skuFormData.name}
                onChange={(e) => setSKUFormData({ ...skuFormData, name: e.target.value })}
                placeholder="e.g., Basic Plan, Monthly"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku-price">Price</Label>
                <Input
                  id="sku-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={skuFormData.price}
                  onChange={(e) => setSKUFormData({ ...skuFormData, price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku-currency">Currency</Label>
                <Select
                  value={skuFormData.currency}
                  onValueChange={(value) => setSKUFormData({ ...skuFormData, currency: value })}
                >
                  <SelectTrigger id="sku-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CNY">CNY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sku-inventory">Inventory (optional)</Label>
              <Input
                id="sku-inventory"
                type="number"
                min="0"
                value={skuFormData.inventory}
                onChange={(e) => setSKUFormData({ ...skuFormData, inventory: e.target.value })}
                placeholder="Leave empty for unlimited"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSKUOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSKUSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : selectedSKU ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete SKU Confirmation */}
      <Dialog open={isDeleteSKUOpen} onOpenChange={setIsDeleteSKUOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete SKU</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSKU?.name}"? This will soft-delete the SKU.
              Existing orders will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteSKUOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSKU} disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
